import React, {useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '../../Icons/SearchIcon';
import NomenclatureSelect from './NomenclatureSelect';
import CallNotification from '../../../context/NotificationContext';
import ApiRequest from '../../StdFunction';
import {IParamsMarkingType, IParamsMeasureUnit, IParamsNomenclature, INomenclatureGroup} from '../../../interfaces/fetch/Nomenclature';
import HelpPopover from '../../../ui/HelpPopover';
import instance from '../../../instance';
import {NomenclatureGroupsManagerPopup} from '../NomenclatureGroupsManagerPopup';
import {COMMODITY_TYPES} from '../commodityTypes';
import {NomenclatureGroupEditPaper} from '../../../ui/NomenclatureGroupEditPaper';
import {ConfirmDialog} from '../../../ui/ConfirmDialog';

interface Props {
    userId: number | null;
    addPositionOpen: boolean;
    setAddPosition: React.Dispatch<React.SetStateAction<boolean>>;
    isNew: boolean;
    activeRow: IParamsNomenclature | null;
    onSaved: (saved: IParamsNomenclature, wasNew: boolean) => void;
    onDeleted: (barcode: string) => void;
    markingTypes: IParamsMarkingType[];
    measureUnits: IParamsMeasureUnit[];
    nomenclatureGroups: INomenclatureGroup[];
    refreshGroups?: () => void;
}

interface IErrorState {
    name?: string;
    barcode?: string;
    ntin?: string;
    vendor_code?: string;
    price?: string;
    measure_unit?: string;
}

const MEASURE_PRIORITY_ORDER = [1, 76, 330, 312, 338, 374, 48, 309, 71, 458, 370, 4, 16, 296];

const NomenclaturePagePopup = ({
    userId,
    addPositionOpen,
    setAddPosition,
    isNew,
    activeRow,
    onSaved,
    onDeleted,
    markingTypes,
    measureUnits,
    nomenclatureGroups,
    refreshGroups,
}: Props) => {
    const {handleErrorNotification} = CallNotification();

    const [errors, setErrors] = useState<IErrorState>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [searchValue, setSearchValue] = useState<string>('');
    const [searchIsLoading, setSearchIsLoading] = useState<boolean>(false);

    const [nomenclatures, setNomenclatures] = useState<any[]>([]);
    const [nomenclatureSelectShow, setNomenclatureSelectShow] = useState(false);
    const [nomenclatureGroupsManagerOpen, setNomenclatureGroupsManagerOpen] = useState(false);

    const [measureUnitId, setMeasureUnitId] = useState<string>('');
    const [nomenclatureGroupId, setNomenclatureGroupId] = useState<number | null>(null);
    const [markingType, setMarkingType] = useState<string>('');
    const [commodityType, setCommodityType] = useState<number | null>(0);
    const [freePrice, setFreePrice] = useState<boolean>(false);
    const [vatInfo, setVatInfo] = useState<{flagVat: 'Y' | 'N' | null; defaultTaxPercent: number | null}>({
        flagVat: null,
        defaultTaxPercent: null,
    });

    const [isSocial, setIsSocial] = useState<boolean>(false);

    const bcSearch = useRef<HTMLInputElement | null>(null);

    const [form, setForm] = useState({
        barcode: '',
        ntin: '',
        name: '',
        vendor_code: '',
        price: '',
        wholesale_price: '',
        tax_percent: '',
    });

    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        title?: string;
        content: React.ReactNode;
        confirmText?: string;
        onConfirm: () => void | Promise<void>;
    }>({
        open: false,
        content: null,
        confirmText: 'Подтвердить',
        onConfirm: () => {},
    });

    useEffect(() => {
        if (!addPositionOpen) return;
        setSearchValue('');
        setSearchIsLoading(false);
        void loadVatInfo();
    }, [userId, addPositionOpen]);

    useEffect(() => {
        if (isNew) {
            setForm({
                barcode: '',
                ntin: '',
                name: '',
                vendor_code: '',
                price: '',
                wholesale_price: '',
                tax_percent: vatInfo.defaultTaxPercent !== null ? String(vatInfo.defaultTaxPercent) : '',
            });
            setMeasureUnitId('');
            setNomenclatureGroupId(null);
            setMarkingType('');
            setCommodityType(0);
            setFreePrice(false);
            setIsSocial(false);
            setIsLoading(false);
        } else if (activeRow) {
            setForm({
                barcode: activeRow.barcode || '',
                ntin: activeRow.ntin || ' ',
                name: activeRow.name || '',
                vendor_code: activeRow.vendor_code || '',
                price: activeRow.price ? String(activeRow.price) : '',
                wholesale_price: activeRow.wholesale_price ? String(activeRow.wholesale_price) : '',
                tax_percent: activeRow.tax_percent ? String(activeRow.tax_percent) : '',
            });
            setMeasureUnitId(activeRow.measure_unit_id ? String(activeRow.measure_unit_id) : '');
            setNomenclatureGroupId(activeRow.nomenclature_group_id ? activeRow.nomenclature_group_id : null);
            setMarkingType(activeRow.marking_type || '');
            setCommodityType(activeRow.commodity_type ?? null);
            setFreePrice(Boolean(activeRow.free_price));
            setIsSocial(Boolean(activeRow.is_social));
            setIsLoading(false);
        }
    }, [isNew, activeRow, vatInfo.defaultTaxPercent]);

    const validate = () => {
        const newErrors: IErrorState = {};
        if (!form.name.trim()) newErrors.name = 'Это поле обязательно';
        if (!form.barcode.trim()) newErrors.barcode = 'Это поле обязательно';
        if (!freePrice && !form.price.trim()) newErrors.price = 'Это поле обязательно';
        if (!measureUnitId) newErrors.measure_unit = 'Это поле обязательно';

        if (isNew && form.ntin.trim()) {
            const ntin = form.ntin.trim();
            if (!/^\d+$/.test(ntin)) {
                newErrors.ntin = 'NTIN должен содержать только цифры';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClose = () => {
        setAddPosition(false);
        setErrors({});
    };

    const handleCancel = () => {
        handleClose();
    };

    const handleSave = async () => {
        if (!validate()) return;

        const params: IParamsNomenclature = {
            user_id: Number(userId),
            name: form.name.trim(),
            barcode: form.barcode.trim(),
            ntin: form.ntin.trim() || '',
            vendor_code: form.vendor_code.trim(),
            price: Number(form.price) || 0,
            wholesale_price: Number(form.wholesale_price) || 0,
            tax_percent: Number(form.tax_percent) || 0,
            free_price: freePrice,
            measure_unit_id: measureUnitId ? Number(measureUnitId) : 0,
            nomenclature_group_id: nomenclatureGroupId ? Number(nomenclatureGroupId) : null,
            marking_type: markingType,
            commodity_type: commodityType,
            is_social: isSocial,
        };

        const method = isNew ? 'CreateNomenclature' : 'EditNomenclature';

        const response = await ApiRequest({method, params}, handleErrorNotification);

        if (!response?.data.error) {
            onSaved(params, isNew);
            handleClose();
        }
    };

    const handleDelete = () => {
        if (!activeRow?.barcode) return;
        const barcode = activeRow.barcode;

        setConfirmState({
            open: true,
            confirmText: 'Удалить',
            content: 'Вы уверены, что хотите удалить эту номенклатуру?',
            onConfirm: async () => {
                setConfirmState(prev => ({...prev, open: false}));
                const response = await ApiRequest(
                    {
                        method: 'DeleteNomenclature',
                        params: {user_id: Number(userId), barcode},
                    },
                    handleErrorNotification,
                );

                if (!response?.data.error) {
                    onDeleted(barcode);
                    handleClose();
                }
            },
        });
    };

    const handleSearchNomenclature = async () => {
        const barcode = searchValue.trim();
        if (!barcode || searchIsLoading) return;

        setSearchIsLoading(true);
        let response;
        try {
            response = await ApiRequest({method: 'LoadNomenclature', params: {barcode, user_id: Number(userId)}}, handleErrorNotification);
        } finally {
            setSearchIsLoading(false);
        }

        if (response?.data?.nomenclature?.length) {
            if (response.data.nomenclature.length === 1) {
                const [first] = response.data.nomenclature;
                setForm({
                    barcode: first.barcode || '',
                    ntin: first.ntin || '',
                    name: first.name || '',
                    vendor_code: first.vendor_code || '',
                    price: first.price ? String(first.price) : '',
                    wholesale_price: first.wholesale_price ? String(first.wholesale_price) : '',
                    tax_percent: first.tax_percent ? String(first.tax_percent) : '',
                });
                setMeasureUnitId(first.measure_unit_id ? String(first.measure_unit_id) : '');
                setNomenclatureGroupId(first.nomenclature_group_id ? first.nomenclature_group_id : null);
                setMarkingType(first.marking_type || '');
                setCommodityType(first.commodity_type ?? null);
                setFreePrice(Boolean(first.free_price));
                setIsSocial(Boolean(first.is_social));
                setSearchValue('');
            } else {
                setForm(prev => ({
                    ...prev,
                    barcode: '',
                    ntin: '',
                    name: '',
                }));
                setNomenclatures(response.data.nomenclature);
                setNomenclatureSelectShow(true);
            }
        } else {
            setForm(prev => ({
                ...prev,
                barcode: '',
                ntin: '',
                name: '',
            }));
        }
    };

    const handleOpenGroupsManager = () => {
        setNomenclatureGroupsManagerOpen(true);
    };

    /**
     * Сохранение карточки по Enter из любого поля формы.
     */
    const handleDialogKeyDown = (e: React.KeyboardEvent) => {
        // defaultPrevented: Enter уже обработан полем (например, выбор опции в Autocomplete).
        if (e.key !== 'Enter' || e.defaultPrevented) return;
        const tagName = (e.target as HTMLElement).tagName;
        if (tagName === 'TEXTAREA' || tagName === 'BUTTON') return;
        // Открыт вложенный попап — Enter относится к нему.
        if (nomenclatureSelectShow || nomenclatureGroupsManagerOpen || confirmState.open) return;
        if (isLoading || isSaveDisabled) return;
        void handleSave();
    };

    const selectedMeasureUnit = measureUnits.find(mu => mu && String(mu.measure_unit_id) === measureUnitId);

    const isSaveDisabled = !form.name.trim() || !form.barcode.trim() || !measureUnitId || (!freePrice && !form.price.trim());

    // Сортируем единицы измерения в приоритете по списку, остальные в алфавитном порядке
    const sortedMeasureUnits = (measureUnits || [])
        .filter(mu => !!mu)
        .sort((a, b) => {
            const indexA = MEASURE_PRIORITY_ORDER.indexOf(a.measure_unit_id);
            const indexB = MEASURE_PRIORITY_ORDER.indexOf(b.measure_unit_id);

            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return a.measure_unit_name.localeCompare(b.measure_unit_name);
        });

    const loadVatInfo = async () => {
        if (!userId || !isNew) return;
        try {
            const response = await instance.post('/LK', {
                method: 'GetVatInfo',
                params: {user_id: Number(userId)},
            });
            if (response?.data) {
                const flag = response.data.vat_info.flag_vat as 'Y' | 'N' | undefined;
                const defaultTax =
                    typeof response.data.vat_info.default_tax_percent === 'number' ? response.data.vat_info.default_tax_percent : null;
                setVatInfo({flagVat: flag ?? null, defaultTaxPercent: defaultTax});
            }
        } catch (error) {
            console.error('Ошибка при получении информации НДС', error);
        }
    };

    return (
        <Dialog
            open={addPositionOpen}
            onClose={handleCancel}
            onKeyDown={handleDialogKeyDown}
            aria-labelledby="nomenclature-dialog-title"
            fullWidth
            maxWidth="sm"
        >
            <Box
                sx={{
                    p: '20px 24px 10px 3px',
                    position: 'relative',
                    backgroundColor: '#f5f7ff',
                }}
            >
                <DialogTitle sx={{pr: 5}}>{isNew ? 'Создать номенклатуру' : 'Редактировать номенклатуру'}</DialogTitle>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {isNew && (
                            <>
                                <Box mb={2}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Шаг 1
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Найдите товар в каталогах ЭКТ и НКТ
                                        <HelpPopover
                                            style={{marginTop: '-2px'}}
                                            content={
                                                <>
                                                    На&nbsp;этом шаге производится поиск по&nbsp;реестрам следующих каталогов товаров.
                                                    <br />
                                                    <br />
                                                    <strong>«Электронный каталог товаров»</strong> (ЭКТ):
                                                    <br />
                                                    <a href={'https://e-catalog.gov.kz'}>https://e-catalog.gov.kz</a>
                                                    <br />
                                                    <br />
                                                    <strong>«Национальный каталог товаров»</strong> (НКТ):
                                                    <br />
                                                    <a href={'https://nct.kz'}>https://nct.kz</a>
                                                    <br />
                                                    <br />
                                                    Если данные доступны в&nbsp;каталогах, они будут использоваться для автозаполнения
                                                    информации о&nbsp;вашем товаре.
                                                </>
                                            }
                                        />
                                    </Typography>
                                </Box>

                                <Box mt={2} mb={3} display="flex" gap={1}>
                                    <TextField
                                        placeholder="Поиск по штрихкоду в каталогах"
                                        inputRef={bcSearch}
                                        value={searchValue}
                                        onChange={e => setSearchValue(e.target.value)}
                                        fullWidth
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                // Иначе Enter в поле поиска сохранит карточку.
                                                e.stopPropagation();
                                                void handleSearchNomenclature();
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={
                                            searchIsLoading ? (
                                                <CircularProgress size="15px" color="inherit" sx={{padding: '5px'}} />
                                            ) : (
                                                <SearchIcon color={!searchValue.trim() ? '#00000042' : undefined} />
                                            )
                                        }
                                        disabled={!searchValue.trim() || searchIsLoading}
                                        onClick={() => void handleSearchNomenclature()}
                                    >
                                        Поиск
                                    </Button>
                                    <NomenclatureSelect
                                        isShow={nomenclatureSelectShow}
                                        setShow={setNomenclatureSelectShow}
                                        nomenclatures={nomenclatures}
                                        setInput={params => {
                                            setForm({
                                                barcode: params.barcode || '',
                                                ntin: params.ntin || '',
                                                name: params.name || '',
                                                vendor_code: params.vendor_code || '',
                                                price: params.price ? String(params.price) : '',
                                                wholesale_price: params.wholesale_price ? String(params.wholesale_price) : '',
                                                tax_percent: params.tax_percent ? String(params.tax_percent) : '',
                                            });
                                            setMeasureUnitId(params.measure_unit_id ? String(params.measure_unit_id) : '');
                                            setNomenclatureGroupId(params.nomenclature_group_id ? params.nomenclature_group_id : null);
                                            setMarkingType(params.marking_type || '');
                                            setCommodityType(params.commodity_type ?? null);
                                            setFreePrice(Boolean(params.free_price));
                                            setIsSocial(Boolean(params.is_social));
                                            setNomenclatureSelectShow(false);
                                        }}
                                    />
                                </Box>

                                <Box mb={2} mt={5}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Шаг 2
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        При необходимости внесите корректировки
                                        <HelpPopover
                                            style={{marginTop: '-2px'}}
                                            content={
                                                <>
                                                    Если в&nbsp;каталогах не&nbsp;найдена информация о&nbsp;товаре (на&nbsp;шаге&nbsp;1),
                                                    заполните необходимые поля вручную.
                                                    <br />
                                                    <br />
                                                    Также вы&nbsp;можете внести изменения, если обнаружена неточность в&nbsp;данных
                                                    из&nbsp;каталога&nbsp;&mdash; например, в&nbsp;названии товара.
                                                </>
                                            }
                                        />
                                    </Typography>
                                </Box>
                            </>
                        )}
                        <Box display="flex" gap={1}>
                            <TextField
                                label="Штрихкод"
                                value={form.barcode}
                                onChange={e => setForm(prev => ({...prev, barcode: e.target.value}))}
                                error={!!errors.barcode}
                                helperText={errors.barcode}
                                required
                                fullWidth
                                margin="dense"
                                disabled={!isNew}
                            />
                            <TextField
                                label="NTIN"
                                value={form.ntin}
                                onChange={e => setForm(prev => ({...prev, ntin: e.target.value}))}
                                error={!!errors.ntin}
                                helperText={errors.ntin}
                                fullWidth
                                margin="dense"
                                disabled={!isNew}
                            />
                        </Box>

                        <TextField
                            label="Наименование"
                            value={form.name}
                            onChange={e => setForm(prev => ({...prev, name: e.target.value}))}
                            error={!!errors.name}
                            helperText={errors.name}
                            fullWidth
                            required
                            margin="dense"
                        />

                        <Box mt={1} mb={1} display="flex" gap={1}>
                            <Autocomplete
                                options={sortedMeasureUnits}
                                getOptionLabel={o => o.measure_unit_name || ''}
                                value={selectedMeasureUnit || null}
                                onChange={(_, val) => setMeasureUnitId(val ? String(val.measure_unit_id) : '')}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Единица измерения"
                                        required
                                        error={!!errors.measure_unit}
                                        helperText={errors.measure_unit}
                                    />
                                )}
                                fullWidth
                            />

                            <TextField
                                label="Признак маркировки"
                                select
                                value={markingType}
                                onChange={e => setMarkingType(e.target.value)}
                                fullWidth
                            >
                                {markingTypes.map(type => (
                                    <MenuItem key={type.marked_type_id} value={type.marked_type_id}>
                                        {type.marked_type_name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                label="Тип позиции"
                                select
                                value={commodityType ?? ''}
                                onChange={e => setCommodityType(e.target.value === '' ? null : Number(e.target.value))}
                                fullWidth
                            >
                                <MenuItem value="">Не задано</MenuItem>
                                {COMMODITY_TYPES.map(type => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        {/*<FormControlLabel*/}
                        {/*    control={<Checkbox checked={isSocial} onChange={e => setIsSocial(e.target.checked)} />}*/}
                        {/*    label="Социальный"*/}
                        {/*/>*/}

                        {isNew && (
                            <Box mb={2} mt={5}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Шаг 3
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Заполните другие данные товара
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            label="Артикул"
                            value={form.vendor_code}
                            onChange={e => setForm(prev => ({...prev, vendor_code: e.target.value}))}
                            fullWidth
                            margin="dense"
                        />

                        <Box display="flex" gap={1}>
                            <TextField
                                label="Цена"
                                value={form.price}
                                onChange={e => setForm(prev => ({...prev, price: e.target.value}))}
                                error={!!errors.price}
                                helperText={errors.price}
                                required={!freePrice}
                                fullWidth
                                margin="dense"
                                type="number"
                            />
                            <TextField
                                label="Оптовая цена"
                                value={form.wholesale_price}
                                onChange={e => setForm(prev => ({...prev, wholesale_price: e.target.value}))}
                                fullWidth
                                margin="dense"
                                type="number"
                            />
                        </Box>

                        <FormControlLabel
                            control={<Checkbox checked={freePrice} onChange={e => setFreePrice(e.target.checked)} />}
                            label="Свободная цена"
                        />

                        <Box display="flex" gap={1}>
                            <TextField
                                label="Процент налога"
                                value={form.tax_percent}
                                onChange={e => setForm(prev => ({...prev, tax_percent: e.target.value}))}
                                fullWidth={false}
                                margin="dense"
                                type="number"
                            />
                        </Box>

                        <Autocomplete
                            options={nomenclatureGroups}
                            PaperComponent={props => <NomenclatureGroupEditPaper {...props} onManageGroups={handleOpenGroupsManager} />}
                            getOptionLabel={option => option.name}
                            value={nomenclatureGroups.find(g => g.id === nomenclatureGroupId) || null}
                            onChange={(event, newValue) => (newValue ? setNomenclatureGroupId(newValue.id) : setNomenclatureGroupId(null))}
                            renderOption={(props, option) => <li {...props}>{option.name}</li>}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Группа номенклатуры"
                                    sx={{
                                        minWidth: 250,
                                        backgroundColor: '#fff',
                                        '.MuiOutlinedInput-root': {
                                            height: '44px',
                                            paddingTop: '0',
                                            paddingBottom: '0',
                                        },
                                    }}
                                />
                            )}
                            disableClearable={nomenclatureGroupId === null}
                            sx={{mt: 1}}
                        />

                        {isNew ? (
                            <Box sx={{mt: 4, mb: 2}}>
                                <Button variant="contained" onClick={() => void handleSave()} sx={{mr: 2}} disabled={isSaveDisabled}>
                                    Добавить
                                </Button>
                                <Button variant="text" onClick={handleCancel}>
                                    Отмена
                                </Button>
                            </Box>
                        ) : (
                            <Grid container justifyContent="space-between" sx={{mt: 4, mb: 2}}>
                                <Grid item>
                                    <Button variant="contained" onClick={() => void handleSave()} sx={{mr: 2}} disabled={isSaveDisabled}>
                                        Сохранить
                                    </Button>
                                    <Button variant="text" onClick={handleCancel}>
                                        Отмена
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="text" color="error" onClick={() => void handleDelete()}>
                                        Удалить
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                        <NomenclatureGroupsManagerPopup
                            open={nomenclatureGroupsManagerOpen}
                            userId={userId}
                            onClose={() => setNomenclatureGroupsManagerOpen(false)}
                            onChange={() => {
                                if (refreshGroups) refreshGroups();
                            }}
                        />
                    </>
                )}
            </DialogContent>
            <ConfirmDialog
                open={confirmState.open}
                onClose={() => setConfirmState(prev => ({...prev, open: false}))}
                onConfirm={confirmState.onConfirm}
                confirmText={confirmState.confirmText}
                content={confirmState.content}
            />
        </Dialog>
    );
};

export default NomenclaturePagePopup;
